/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, DatePicker, Space, Typography, Alert, Tag } from 'antd';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import dayjs from 'dayjs';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetEventTemplatesQuery, useGetEventTemplateByIdQuery } from '../../api/eventTemplatesApi';
import { useCreateEventFromTemplateMutation } from '../../api/plantingEventsApi';
import { useGetSaveUsersQuery } from '../../api/saveUsersApi';
import { toastError, toastSuccess } from '../../utils/toast';
import type { EventTemplateCategory } from '../../types/eventTemplates';
import { useSave } from '../../contexts/WorldContext';
import { useOpenDay } from '../../hooks/useOpenDay';
import { disableBeforeOpenDay } from '../../utils/openDayDate';
import { useTranslation } from 'react-i18next';
import { datePickerFormat } from '../../i18n';
import { categoryLabel } from './ActionWizard.types';

const { TextArea } = Input;
const { Text } = Typography;

const styles = {
  iconLarge: css({ fontSize: '20px' }),
  customTag: css({ marginLeft: 4 }),
  listItem: css({ marginBottom: 0, paddingLeft: 20 }),
  formSection: css({ marginTop: 16 }),
  infoAlert: css({ marginTop: 8 }),
};

interface CreateBatchEventModalProps {
  farmId: string;
  plantingIds: string[]; // Массив UUID посадок
  bedIds?: number[]; // Опционально - массив ID грядок
  plotIds?: number[]; // Опционально - массив ID участков
  fieldIds?: number[]; // Опционально - массив ID полей
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Модальное окно для создания BATCH (групповых) событий из шаблона (CN-0008)
 * 
 * Особенности:
 * - Принимает множественные цели (plantingIds/bedIds/plotIds/fieldIds)
 * - Автоматически создает parent + children события на бэкенде
 * - Parent событие отображается в очереди с прогрессом
 * - Children события выполняются индивидуально
 * 
 * Workflow:
 * 1. Пользователь выбирает несколько целей (чекбоксы в таблице)
 * 2. Кликает "Массовые мероприятия"
 * 3. Выбирает шаблон и назначает исполнителя
 * 4. Бэкенд создает 1 parent + N children событий
 * 5. В очереди показывается только parent с прогрессом
 */
const CreateBatchEventModal: React.FC<CreateBatchEventModalProps> = ({
  farmId,
  plantingIds = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // saveId resolves through useSave() (Phase 4 — save-scope events).
  bedIds = [],
  plotIds = [],
  fieldIds = [],
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('events');
  const save = useSave();
  const openDay = useOpenDay();
  const [form] = Form.useForm();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Вычисляем общее количество целей
  const totalTargets = plantingIds.length + bedIds.length + plotIds.length + fieldIds.length;

  // Загружаем шаблоны событий
  const {
    data: templatesData,
    isLoading: loadingTemplates,
  } = useGetEventTemplatesQuery(
    { saveId: save?.id || '', isActive: true },
    { skip: !open || !save?.id }
  );
  const templates = templatesData?.templates ?? [];

  // Загружаем детали выбранного шаблона
  const {
    data: templateDetail,
  } = useGetEventTemplateByIdQuery(
    selectedTemplateId && open && save?.id ? { id: selectedTemplateId, saveId: save.id } : skipToken
  );

  // Загружаем пользователей сейва для назначения
  const {
    data: farmUsers = [],
    isLoading: loadingUsers,
  } = useGetSaveUsersQuery(save.id, { 
    skip: !open || !selectedTemplateId || !templateDetail?.assignable 
  });

  const [createBatchEvent, { isLoading: creating }] = useCreateEventFromTemplateMutation();

  // Сбросить форму при открытии
  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedTemplateId(null);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedTemplateId) {
        toastError(t('createTask.selectTemplateError'));
        return;
      }

      if (totalTargets === 0) {
        toastError(t('createBatch.noTargets'));
        return;
      }

      // Формируем массив targets из всех типов целей
      const targets: Array<{
        plantingId?: string;
        bedId?: string;
        plotId?: string;
        fieldId?: string;
      }> = [
        ...plantingIds.map(id => ({ plantingId: id })),
        ...bedIds.map(id => ({ bedId: String(id) })),
        ...plotIds.map(id => ({ plotId: String(id) })),
        ...fieldIds.map(id => ({ fieldId: String(id) })),
      ];

      // Формируем payload для создания batch события
      const payload = {
        templateId: selectedTemplateId,
        farmId,
        targets,
        plannedDate: values.plannedDate ? dayjs(values.plannedDate).format('YYYY-MM-DD') : undefined,
        notes: values.notes || undefined,
        assignedTo: values.assignedTo || undefined,
      };

      // Используем useSave context для resolve'а saveId. Phase 4: events живут в save-scope.
      const result = await createBatchEvent({ saveId: save.id, payload }).unwrap();
      
      const eventCount = result.events.length;
      const isBatch = eventCount > 1;
      
      if (isBatch) {
        toastSuccess(
          t('createBatch.createdMany', { count: eventCount })
        );
      } else {
        toastSuccess(t('createBatch.createdOne'));
      }
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error creating batch event:', error);
      toastError(error?.data || error?.response ? error : (error?.message || t('createBatch.failed')));
    }
  };

  // Группировка шаблонов по категориям
  const templatesByCategory = templates.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<EventTemplateCategory, typeof templates>);

  const categoryOrder: EventTemplateCategory[] = [
    'inspection', 'measurement', 'treatment', 'care', 'harvest', 'maintenance', 'lifecycle',
  ];

  return (
    <Modal
      title={t('createBatch.title')}
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText={t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={creating}
      width={650}
    >
      <Space direction="vertical" size="middle" css={commonStyles.fullWidth}>
        <Alert
          type="info"
          message={t('createBatch.selectedTargets', { count: totalTargets })}
          description={
            <div>
              {plantingIds.length > 0 && <div>{t('createBatch.plantingsCount', { count: plantingIds.length })}</div>}
              {bedIds.length > 0 && <div>{t('createBatch.bedsCount', { count: bedIds.length })}</div>}
              {plotIds.length > 0 && <div>{t('createBatch.plotsCount', { count: plotIds.length })}</div>}
              {fieldIds.length > 0 && <div>{t('createBatch.fieldsCount', { count: fieldIds.length })}</div>}
              <div css={styles.infoAlert}>
                {totalTargets > 1
                  ? t('createBatch.willCreateBatch')
                  : t('createBatch.willCreateSingle')}
              </div>
            </div>
          }
          showIcon
        />

        {selectedTemplateId && templateDetail && (
          <Alert
            type="success"
            message={
              <Space>
                {templateDetail.icon && <span css={styles.iconLarge}>{templateDetail.icon}</span>}
                <Text strong>{templateDetail.name}</Text>
                {templateDetail.scopeType === 'farm' && (
                  <Tag color="purple">{t('createTask.customTag')}</Tag>
                )}
              </Space>
            }
            description={templateDetail.description}
            showIcon={false}
          />
        )}
      </Space>

      <Form form={form} layout="vertical" css={styles.formSection}>
        {/* Выбор шаблона - ОБЯЗАТЕЛЬНО */}
        <Form.Item
          label={t('createEvent.template')}
          name="templateId"
          rules={[{ required: true, message: t('createTask.templateRequired') }]}
          help={t('createBatch.templateHelp')}
        >
          <Select
            placeholder={t('createTask.templatePlaceholder')}
            loading={loadingTemplates}
            value={selectedTemplateId}
            onChange={(val) => {
              setSelectedTemplateId(val);
              form.setFieldValue('assignedTo', undefined); // Сбросить исполнителя при смене шаблона
            }}
            showSearch
            optionFilterProp="children"
          >
            {categoryOrder.map((category) => {
              const categoryName = categoryLabel(category);
              const categoryTemplates = templatesByCategory[category as EventTemplateCategory];
              if (!categoryTemplates || categoryTemplates.length === 0) return null;

              return (
                <Select.OptGroup key={category} label={categoryName}>
                  {categoryTemplates.map((template) => (
                    <Select.Option key={template.id} value={template.id}>
                      <Space>
                        {template.icon && <span>{template.icon}</span>}
                        <span>{template.name}</span>
                        {template.scopeType === 'farm' && (
                          <Tag color="purple" css={styles.customTag}>{t('actionGrid.custom')}</Tag>
                        )}
                      </Space>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              );
            })}
          </Select>
        </Form.Item>

        {/* Назначение исполнителя (только если assignable) */}
        {selectedTemplateId && templateDetail?.assignable && (
          <Form.Item
            label={t('createTask.assignee')}
            name="assignedTo"
            help={t('createBatch.assigneeHelp')}
          >
            <Select
              placeholder={t('createTask.assigneePlaceholder')}
              allowClear
              loading={loadingUsers}
              showSearch
              optionFilterProp="children"
            >
              {farmUsers.map((farmUser) => (
                <Select.Option key={farmUser.user.id} value={farmUser.user.id}>
                  <Space>
                    <Text>{farmUser.user.username || farmUser.user.email || farmUser.user.id}</Text>
                    <Tag color="blue">{farmUser.membership.role.code}</Tag>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* Дата планирования */}
        <Form.Item
          label={t('createTask.plannedDate')}
          name="plannedDate"
          help={t('createBatch.plannedDateHelp')}
        >
          <DatePicker
            css={commonStyles.fullWidth}
            placeholder={t('form.selectDate')}
            format={datePickerFormat()}
            disabledDate={disableBeforeOpenDay(openDay)}
          />
        </Form.Item>

        {/* Примечания */}
        <Form.Item
          label={t('form.notes')}
          name="notes"
        >
          <TextArea
            rows={3}
            placeholder={t('createBatch.notesPlaceholder')}
          />
        </Form.Item>

        {/* Информация об атрибутах шаблона */}
        {templateDetail && templateDetail.properties && templateDetail.properties.length > 0 && (
          <Alert
            type="info"
            message={t('createTask.parametersToFill')}
            description={
              <ul css={styles.listItem}>
                {templateDetail.properties.map((attr) => (
                  <li key={attr.propertyId}>
                    <Text>{attr.name}</Text>
                    {attr.isRequired && <Text type="danger"> *</Text>}
                    {attr.unit && <Text type="secondary"> ({attr.unit})</Text>}
                  </li>
                ))}
              </ul>
            }
          />
        )}
      </Form>
    </Modal>
  );
};

export default CreateBatchEventModal;
